class CollectionNavigation {
  constructor() {
    this.nav = document.querySelector('.collection-nav');
    this.navLinks = document.querySelectorAll('.collection-nav__item');
    this.lastScrollTop = 0;
    this.hasScrolledPast = false;
    this.sections = Array.from(document.querySelectorAll('.collection-block'));
    this.currentSection = null;
    this.isInitialLoad = true; // Add flag to track initial load

    this.init();
  }

  init() {
    this.setupEventListeners();
    this.updateActiveSection();
    // Set flag to false after initial setup
    setTimeout(() => {
      this.isInitialLoad = false;
    }, 100);
  }

  setupEventListeners() {
    this.setupScrollListener();
    this.setupNavLinks();
    // The header's painted height feeds the active-section threshold.
    document.addEventListener('sticky-stack:change', () => this.updateActiveSection());
  }

  setupScrollListener() {
    window.addEventListener('scroll', () => {
      this.handleScroll();
      this.updateActiveSection();
    });
  }

  setupNavLinks() {
    this.navLinks.forEach(button => {
      button.addEventListener('click', (e) => this.handleNavClick(e));
      button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleNavClick(e);
        }
      });
    });
  }

  handleScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const navBottom = this.nav.offsetTop + this.nav.offsetHeight;

    this.updateScrollPastState(scrollTop, navBottom);
    this.updateScrollDirection(scrollTop);
    
    this.lastScrollTop = scrollTop;
  }

  updateScrollPastState(scrollTop, navBottom) {
    if (scrollTop > navBottom) {
      this.hasScrolledPast = true;
      this.nav.classList.add('has-scrolled-past');
    } else {
      this.hasScrolledPast = false;
      this.nav.classList.remove('has-scrolled-past');
    }
  }

  updateScrollDirection(scrollTop) {
    if (scrollTop > this.lastScrollTop) {
      this.nav.classList.add('scrolling-down');
      this.nav.classList.remove('scrolling-up');
    } else {
      this.nav.classList.add('scrolling-up');
      this.nav.classList.remove('scrolling-down');
    }
  }

  /**
   * Height of everything pinned above the collection content: promo bar and
   * header (published as CSS variables by sticky-stack.js), the mobile
   * landing-filter bar and the nav itself.
   */
  stickyOffset() {
    const root = getComputedStyle(document.documentElement);
    const px = (name) => parseFloat(root.getPropertyValue(name)) || 0;
    return px('--header-offset') + px('--size-modal-height') + (this.nav?.offsetHeight || 0);
  }

  updateActiveSection() {
    const scrollPosition = window.scrollY + this.stickyOffset() + 100;

    let currentSection = null;
    let minDistance = Infinity;

    this.sections.forEach(section => {
      const sectionTop = section.offsetTop;
      const sectionBottom = sectionTop + section.offsetHeight;
      const distance = Math.abs(scrollPosition - sectionTop);

      if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
        currentSection = section;
      }
      else if (!currentSection && scrollPosition < sectionTop && distance < minDistance) {
        currentSection = section;
        minDistance = distance;
      }
    });

    if (currentSection !== this.currentSection) {
      this.currentSection = currentSection;
      this.updateActiveLink();
    }
  }

  updateActiveLink() {
    const activeTarget = this.currentSection ? `#${this.currentSection.id}` : null;
    this.navLinks.forEach(button => {
      const isActive = button.getAttribute('data-target') === activeTarget;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive);
      if (isActive && window.innerWidth <= 749 && !this.isInitialLoad) {
        this.centerTab(button);
      }
    });
  }

  // Centres the active tab by scrolling the strip itself. scrollIntoView would
  // also scroll the document whenever the nav is off-screen.
  centerTab(button) {
    const list = button.closest('.collection-nav__list');
    if (!list || list.scrollWidth <= list.clientWidth) return;
    const offset = button.getBoundingClientRect().left - list.getBoundingClientRect().left;
    list.scrollTo({
      left: list.scrollLeft + offset - (list.clientWidth - button.offsetWidth) / 2,
      behavior: 'smooth',
    });
  }

  handleNavClick(event) {
    event.preventDefault();
    const targetId = event.currentTarget.getAttribute('data-target');
    const targetElement = document.querySelector(targetId);
    
    if (targetElement) {
      this.scrollToTarget(targetElement);
      this.navLinks.forEach(button => {
        const isActive = button === event.currentTarget;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive);
      });
    }
  }

  scrollToTarget(targetElement) {
    const elementPosition = targetElement.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - this.stickyOffset();

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
}

// Initialize the navigation when the DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('.collection-nav')) {
    new CollectionNavigation();
  }
});